/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Present when state is broken. audience_mismatch: legacy value, no longer emitted; the administrator-confirmed Okta audience may differ from the server's authorization server issuer; scope_not_allowed: the connection does not allow the requested scopes; client_auth_failed: the identity provider rejected the agent app's client authentication; downstream_rejected: the identity provider issued the assertion but the server's authorization server refused it.
 */
export const BrokenReason = {
  AudienceMismatch: "audience_mismatch",
  ScopeNotAllowed: "scope_not_allowed",
  ClientAuthFailed: "client_auth_failed",
  DownstreamRejected: "downstream_rejected",
} as const;
/**
 * Present when state is broken. audience_mismatch: legacy value, no longer emitted; the administrator-confirmed Okta audience may differ from the server's authorization server issuer; scope_not_allowed: the connection does not allow the requested scopes; client_auth_failed: the identity provider rejected the agent app's client authentication; downstream_rejected: the identity provider issued the assertion but the server's authorization server refused it.
 */
export type BrokenReason = OpenEnum<typeof BrokenReason>;

/**
 * bound: an explicit identity chaining binding selects the client; single: the one client attached to the authorization server; ambiguous: more than one candidate and no single binding; missing: no client registered yet.
 */
export const ClientBinding = {
  Bound: "bound",
  Single: "single",
  Ambiguous: "ambiguous",
  Missing: "missing",
} as const;
/**
 * bound: an explicit identity chaining binding selects the client; single: the one client attached to the authorization server; ambiguous: more than one candidate and no single binding; missing: no client registered yet.
 */
export type ClientBinding = OpenEnum<typeof ClientBinding>;

/**
 * no_idjag: the server's authorization server metadata does not advertise the identity assertion grant.
 */
export const NotApplicableReason = {
  NoIdjag: "no_idjag",
} as const;
/**
 * no_idjag: the server's authorization server metadata does not advertise the identity assertion grant.
 */
export type NotApplicableReason = OpenEnum<typeof NotApplicableReason>;

/**
 * What the latest identity chaining exchange since confirmation showed. connection_missing is inferred from the identity provider's invalid_target error.
 */
export const ObservedResult = {
  Verified: "verified",
  DownstreamRejected: "downstream_rejected",
  ConnectionMissing: "connection_missing",
  ScopeNotAllowed: "scope_not_allowed",
  ClientAuthFailed: "client_auth_failed",
} as const;
/**
 * What the latest identity chaining exchange since confirmation showed. connection_missing is inferred from the identity provider's invalid_target error.
 */
export type ObservedResult = OpenEnum<typeof ObservedResult>;

/**
 * Derived readiness. not_applicable: see not_applicable_reason; needs_agent: no AI agent recorded on the connection; needs_connection: the agent-to-resource connection has not been confirmed for this server's upstream, or an exchange since confirmation found it missing; broken: confirmed but known not to work, see broken_reason; connected: the administrator confirmed it and no conclusive exchange has run since; verified: an exchange since confirmation succeeded.
 */
export const OktaResourceConnectionServerState = {
  NotApplicable: "not_applicable",
  NeedsAgent: "needs_agent",
  NeedsConnection: "needs_connection",
  Broken: "broken",
  Connected: "connected",
  Verified: "verified",
} as const;
/**
 * Derived readiness. not_applicable: see not_applicable_reason; needs_agent: no AI agent recorded on the connection; needs_connection: the agent-to-resource connection has not been confirmed for this server's upstream, or an exchange since confirmation found it missing; broken: confirmed but known not to work, see broken_reason; connected: the administrator confirmed it and no conclusive exchange has run since; verified: an exchange since confirmation succeeded.
 */
export type OktaResourceConnectionServerState = OpenEnum<
  typeof OktaResourceConnectionServerState
>;

/**
 * One MCP server and the Okta resource connection recorded for its upstream, with the derived Cross App Access state and the exact values the administrator enters in the identity provider console. Servers that share an upstream resource share one Okta resource connection.
 */
export type OktaResourceConnectionServer = {
  /**
   * The resource app's Cross App Access issuer URL as configured in the identity provider; the audience of the identity assertion. Present once confirmed.
   */
  audience?: string | undefined;
  /**
   * The server's authorization server issuer; the Issuer URL to enter when enabling Cross App Access on a resource app. Omitted when unknown.
   */
  authorizationServerIssuer?: string | undefined;
  /**
   * Present when state is broken. audience_mismatch: legacy value, no longer emitted; the administrator-confirmed Okta audience may differ from the server's authorization server issuer; scope_not_allowed: the connection does not allow the requested scopes; client_auth_failed: the identity provider rejected the agent app's client authentication; downstream_rejected: the identity provider issued the assertion but the server's authorization server refused it.
   */
  brokenReason?: BrokenReason | undefined;
  /**
   * bound: an explicit identity chaining binding selects the client; single: the one client attached to the authorization server; ambiguous: more than one candidate and no single binding; missing: no client registered yet.
   */
  clientBinding: ClientBinding;
  /**
   * Speakeasy's client ID at the server's authorization server. Omitted when no client is bound yet.
   */
  clientId?: string | undefined;
  /**
   * When the administrator confirmed the connection for this server's upstream.
   */
  confirmedAt?: Date | undefined;
  /**
   * Link to the add-resource-connection form of the AI agent in the identity provider console. Omitted until an agent is recorded or when the org uses a custom domain.
   */
  deepLink?: string | undefined;
  /**
   * The upstream authorization server ID. Together with the resource indicator, identifies the shared readiness confirmation; independent of the confirmed identity assertion audience.
   */
  issuerId?: string | undefined;
  /**
   * MCP server ID.
   */
  mcpServerId: string;
  /**
   * no_idjag: the server's authorization server metadata does not advertise the identity assertion grant.
   */
  notApplicableReason?: NotApplicableReason | undefined;
  /**
   * When the exchange behind observed_result started.
   */
  observedAt?: Date | undefined;
  /**
   * What the latest identity chaining exchange since confirmation showed. connection_missing is inferred from the identity provider's invalid_target error.
   */
  observedResult?: ObservedResult | undefined;
  /**
   * The identity provider app instance the administrator picked for this upstream, when recorded.
   */
  oktaApplicationId?: string | undefined;
  /**
   * Label of that app instance in the latest applications snapshot; omitted when the instance is no longer there.
   */
  oktaApplicationLabel?: string | undefined;
  /**
   * Whether the administrator still has a step to do for this server.
   */
  pending: boolean;
  /**
   * Project the server belongs to.
   */
  projectId: string;
  projectSlug: string;
  /**
   * The resource indicator to enter on the connection: the server's RFC 9728 resource identifier when known, otherwise its URL.
   */
  resourceIndicator: string;
  /**
   * Scopes Speakeasy requests at the resource.
   */
  scopes: Array<string>;
  /**
   * Server display name; admin-editable, never a key.
   */
  serverName: string;
  serverSlug: string;
  /**
   * Derived readiness. not_applicable: see not_applicable_reason; needs_agent: no AI agent recorded on the connection; needs_connection: the agent-to-resource connection has not been confirmed for this server's upstream, or an exchange since confirmation found it missing; broken: confirmed but known not to work, see broken_reason; connected: the administrator confirmed it and no conclusive exchange has run since; verified: an exchange since confirmation succeeded.
   */
  state: OktaResourceConnectionServerState;
};

/** @internal */
export const BrokenReason$inboundSchema: z.ZodMiniType<BrokenReason, unknown> =
  openEnums.inboundSchema(BrokenReason);

/** @internal */
export const ClientBinding$inboundSchema: z.ZodMiniType<
  ClientBinding,
  unknown
> = openEnums.inboundSchema(ClientBinding);

/** @internal */
export const NotApplicableReason$inboundSchema: z.ZodMiniType<
  NotApplicableReason,
  unknown
> = openEnums.inboundSchema(NotApplicableReason);

/** @internal */
export const ObservedResult$inboundSchema: z.ZodMiniType<
  ObservedResult,
  unknown
> = openEnums.inboundSchema(ObservedResult);

/** @internal */
export const OktaResourceConnectionServerState$inboundSchema: z.ZodMiniType<
  OktaResourceConnectionServerState,
  unknown
> = openEnums.inboundSchema(OktaResourceConnectionServerState);

/** @internal */
export const OktaResourceConnectionServer$inboundSchema: z.ZodMiniType<
  OktaResourceConnectionServer,
  unknown
> = z.pipe(
  z.object({
    audience: z.optional(z.string()),
    authorization_server_issuer: z.optional(z.string()),
    broken_reason: z.optional(BrokenReason$inboundSchema),
    client_binding: ClientBinding$inboundSchema,
    client_id: z.optional(z.string()),
    confirmed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    deep_link: z.optional(z.string()),
    issuer_id: z.optional(z.string()),
    mcp_server_id: z.string(),
    not_applicable_reason: z.optional(NotApplicableReason$inboundSchema),
    observed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    observed_result: z.optional(ObservedResult$inboundSchema),
    okta_application_id: z.optional(z.string()),
    okta_application_label: z.optional(z.string()),
    pending: z.boolean(),
    project_id: z.string(),
    project_slug: z.string(),
    resource_indicator: z.string(),
    scopes: z.array(z.string()),
    server_name: z.string(),
    server_slug: z.string(),
    state: OktaResourceConnectionServerState$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "authorization_server_issuer": "authorizationServerIssuer",
      "broken_reason": "brokenReason",
      "client_binding": "clientBinding",
      "client_id": "clientId",
      "confirmed_at": "confirmedAt",
      "deep_link": "deepLink",
      "issuer_id": "issuerId",
      "mcp_server_id": "mcpServerId",
      "not_applicable_reason": "notApplicableReason",
      "observed_at": "observedAt",
      "observed_result": "observedResult",
      "okta_application_id": "oktaApplicationId",
      "okta_application_label": "oktaApplicationLabel",
      "project_id": "projectId",
      "project_slug": "projectSlug",
      "resource_indicator": "resourceIndicator",
      "server_name": "serverName",
      "server_slug": "serverSlug",
    });
  }),
);

export function oktaResourceConnectionServerFromJSON(
  jsonString: string,
): SafeParseResult<OktaResourceConnectionServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => OktaResourceConnectionServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'OktaResourceConnectionServer' from JSON`,
  );
}
